import { useMemo, useRef, useState } from 'react';
import { useAuth } from './routes/MyContextProvider';
import { useTranslation } from 'react-i18next';
import './Subscription_Management.css';

const RTL_LANGUAGE_PREFIXES = ['ar', 'fa', 'he', 'ur'];

const US_STATE_AND_TERRITORY_OPTIONS = [
  'Alabama',
  'Alaska',
  'American Samoa',
  'Arizona',
  'Arkansas',
  'California',
  'Colorado',
  'Connecticut',
  'Delaware',
  'District of Columbia',
  'Florida',
  'Georgia',
  'Guam',
  'Hawaii',
  'Idaho',
  'Illinois',
  'Indiana',
  'Iowa',
  'Kansas',
  'Kentucky',
  'Louisiana',
  'Maine',
  'Marshall Islands',
  'Maryland',
  'Massachusetts',
  'Michigan',
  'Micronesia',
  'Minnesota',
  'Mississippi',
  'Missouri',
  'Montana',
  'Nebraska',
  'Nevada',
  'New Hampshire',
  'New Jersey',
  'New Mexico',
  'New York',
  'North Carolina',
  'North Dakota',
  'Northern Mariana Islands',
  'Ohio',
  'Oklahoma',
  'Oregon',
  'Palau',
  'Pennsylvania',
  'Puerto Rico',
  'Rhode Island',
  'South Carolina',
  'South Dakota',
  'Tennessee',
  'Texas',
  'U.S. Minor Outlying Islands',
  'U.S. Virgin Islands',
  'Utah',
  'Vermont',
  'Virginia',
  'Washington',
  'West Virginia',
  'Wisconsin',
  'Wyoming',
];

const ROLE_OPTIONS = [
  {
    id: 'role_1_public_visitor',
    number: '1',
    title: 'Public visitor',
    description:
      'Free public access with limited listings, approximate map view, no precise location data, no full documents, no review request, and no buy or sell action.',
  },
  {
    id: 'role_2_verified_investor_marketplace_admin',
    number: '2',
    title: 'Verified investor, sponsor admin, offering admin, or marketplace admin',
    description:
      'After identity verification and eligibility review, approved users may view full listing details, request reviews, and perform allowed buy or sell actions within the limits of each offering.',
  },
  {
    id: 'role_3_broker_dealer_transfer_agent',
    number: '3',
    title: 'Broker-dealer signer, transfer agent, or token operations user',
    description:
      'For licensed or approved transaction, transfer, and token operation users. Any transaction-related compensation must be shown separately and handled under the applicable agreement and legal requirements.',
  },
  {
    id: 'role_4_platform_admin_map_data',
    number: '4',
    title: 'Platform admin, system admin, map data operator, or map data reviewer',
    description:
      'For internal or approved administrative users who manage platform data, user roles, map records, listings, and review workflows. Direct trading requires a separate approved investor role.',
  },
  {
    id: 'role_5_escrow_agent',
    number: '5',
    title: 'Escrow agent or escrow administrator',
    description:
      'For approved escrow service users who manage escrow setup, deposits, releases, refunds, and distribution payment status. This role does not provide direct buy or sell access.',
  },
  {
    id: 'role_6_professional_reviewers',
    number: '6',
    title: 'Professional reviewers',
    description:
      'For inspectors, valuation reviewers, compliance reviewers, title reviewers, auction source reviewers, financial analysts, investor relations reviewers, and other assigned professional review users.',
  },
  {
    id: 'role_7_legal_counsel',
    number: '7',
    title: 'Legal counsel, securities attorney, or legal administrator',
    description:
      'For approved legal users who may review, approve, reject, or upload legal documents, disclosures, legal opinions, exemption notes, and related legal review records.',
  },
  {
    id: 'role_8_asset_owner_operator',
    number: '8',
    title: 'Asset owner, sponsor, issuer, property manager, asset manager, or rehab manager',
    description:
      'For project-side users who want to add assets or tokens, create listings, manage a data room, request reviews, coordinate professional services, and select a platform service plan.',
  },
  {
    id: 'role_9_external_token_listing',
    number: '9',
    title: 'External token issuer or external token listing partner',
    description:
      'For organizations that already tokenized assets outside this platform and want those tokens to be reviewed, listed, displayed, or routed through platform access and reporting workflows.',
  },
  {
    id: 'role_10_other_professional_services',
    number: '10',
    title: 'Other professional services',
    description:
      'For tax reporting, audit trail, reconciliation, distribution reporting, financial controls, or other professional services required across multiple workflows.',
  },
];

const ASSET_OWNER_PLANS = [
  {
    id: 'starter',
    title: 'Starter',
    price: '$500 / month / token',
    description:
      'Basic SaaS management service for each active token. Suitable for a small number of assets or an initial launch.',
  },
  {
    id: 'professional',
    title: 'Professional',
    price: '$3,500 / month / 10 tokens',
    description:
      'Portfolio-level SaaS management service for a minimum of ten active tokens with a lower effective cost per token.',
  },
  {
    id: 'enterprise',
    title: 'Enterprise / Custom',
    price: 'From $5,000 / month',
    description:
      'Custom deployment and support for an enterprise customer. Sales responsibility, platform display, data support, listing support, and allowed transaction fees must be defined in a separate agreement.',
  },
];

const EXTERNAL_TOKEN_STATUS_OPTIONS = [
  'External review',
  'Verified external listing',
  'Active listing',
  'Display only',
  'Request info only',
  'Secondary transfer restricted',
];

const getInitialFormData = (user) => ({
  firstName: user?.firstName || '',
  lastName: user?.lastName || '',
  email: user?.email || '',
  password: '',
  confirmPassword: '',
  phone: '',
  companyName: '',
  stateOrTerritory: '',
  notes: '',
  selectedRoles: [],
  assetOwnerPlan: '',
  externalTokenStatus: '',
  externalTokenIssuer: '',
  externalTokenNetwork: '',
  externalTokenContractAddress: '',
  externalTokenStandard: '',
  externalLedgerReference: '',
});

const SubscriptionPage = () => {
  const { t, i18n } = useTranslation();
  const { isLoggedIn, user } = useAuth();
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState(() => getInitialFormData(user));
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [message, setMessage] = useState('');

  const currentLanguage = (i18n?.language || 'en').toLowerCase();
  const pageDirection = RTL_LANGUAGE_PREFIXES.some((prefix) => currentLanguage.startsWith(prefix))
    ? 'rtl'
    : 'ltr';

  const hasAssetOwnerRole = useMemo(
    () => formData.selectedRoles.includes('role_8_asset_owner_operator'),
    [formData.selectedRoles]
  );

  const hasExternalTokenRole = useMemo(
    () => formData.selectedRoles.includes('role_9_external_token_listing'),
    [formData.selectedRoles]
  );

  const updateField = (fieldName, value) => {
    setFormData((current) => ({
      ...current,
      [fieldName]: value,
    }));
  };

  const toggleRole = (roleId) => {
    setFormData((current) => {
      const selectedRoles = current.selectedRoles.includes(roleId)
        ? current.selectedRoles.filter((item) => item !== roleId)
        : [...current.selectedRoles, roleId];

      return {
        ...current,
        selectedRoles,
        assetOwnerPlan: selectedRoles.includes('role_8_asset_owner_operator')
          ? current.assetOwnerPlan
          : '',
        externalTokenStatus: selectedRoles.includes('role_9_external_token_listing')
          ? current.externalTokenStatus
          : '',
        externalTokenIssuer: selectedRoles.includes('role_9_external_token_listing')
          ? current.externalTokenIssuer
          : '',
        externalTokenNetwork: selectedRoles.includes('role_9_external_token_listing')
          ? current.externalTokenNetwork
          : '',
        externalTokenContractAddress: selectedRoles.includes('role_9_external_token_listing')
          ? current.externalTokenContractAddress
          : '',
        externalTokenStandard: selectedRoles.includes('role_9_external_token_listing')
          ? current.externalTokenStandard
          : '',
        externalLedgerReference: selectedRoles.includes('role_9_external_token_listing')
          ? current.externalLedgerReference
          : '',
      };
    });
  };

  const addFiles = (fileList) => {
    const newFiles = Array.from(fileList || []);

    if (!newFiles.length) {
      return;
    }

    setUploadedFiles((currentFiles) => {
      const existingKeys = new Set(
        currentFiles.map((file) => `${file.name}-${file.size}-${file.lastModified}`)
      );

      const uniqueFiles = newFiles.filter(
        (file) => !existingKeys.has(`${file.name}-${file.size}-${file.lastModified}`)
      );

      return [...currentFiles, ...uniqueFiles];
    });
  };

  const removeFile = (fileIndex) => {
    setUploadedFiles((currentFiles) => currentFiles.filter((_, index) => index !== fileIndex));
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    addFiles(event.dataTransfer.files);
  };

  const handleFilePickerChange = (event) => {
    addFiles(event.target.files);
    event.target.value = '';
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!formData.stateOrTerritory) {
      setMessage(t('Please select a U.S. state, territory, or related jurisdiction.'));
      return;
    }

    if (!formData.selectedRoles.length) {
      setMessage(t('Please select at least one user type or role.'));
      return;
    }

    if (hasAssetOwnerRole && !formData.assetOwnerPlan) {
      setMessage(t('Please select a service plan for the asset owner, sponsor, or issuer role.'));
      return;
    }

    const requestPayload = {
      ...formData,
      isLoggedIn,
      uploadedFiles: uploadedFiles.map((file) => ({
        name: file.name,
        size: file.size,
        type: file.type,
      })),
    };

    console.log('Pricing access request payload:', requestPayload);

    setMessage(
      t('Your access and pricing request has been received. Our team will review the information and contact you with the next steps.')
    );
  };

  return (
    <div className="auth-container pricing-page" dir={pageDirection}>
      <form className="auth-form pricing-form" onSubmit={handleSubmit} dir={pageDirection}>
        <header className="pricing-hero">
          <p className="pricing-kicker">{t('Stark RWAE Exchange')}</p>
          <h1>{t('Pricing & Access Request')}</h1>
          <p>
            {t(
              'Request access to platform services for listing, review, document management, reporting, and token-related workflows. Access and service availability depend on verification, role approval, applicable agreements, and legal requirements.'
            )}
          </p>
        </header>

        <section className="pricing-info-grid" aria-label={t('Service overview')}>
          <article className="pricing-info-card">
            <h3>{t('Market access')}</h3>
            <p>
              {t(
                'Public users can view limited information. Verified users may request broader access based on their role and approval status.'
              )}
            </p>
          </article>

          <article className="pricing-info-card">
            <h3>{t('Project services')}</h3>
            <p>
              {t(
                'Sponsors, issuers, asset operators, and external listing partners can request platform services for asset and token workflows.'
              )}
            </p>
          </article>

          <article className="pricing-info-card">
            <h3>{t('Professional review')}</h3>
            <p>
              {t(
                'Approved professionals may support review, legal, escrow, compliance, valuation, reporting, and other assigned service workflows.'
              )}
            </p>
          </article>
        </section>

        <section className="pricing-section">
          <div className="section-heading">
            <span>{t('1')}</span>
            <div>
              <h2>{t('Registration and contact information')}</h2>
              <p>{t('Enter the information needed to create or update your access request profile.')}</p>
            </div>
          </div>

          <div className="form-grid two-columns">
            <div className="form-group">
              <label htmlFor="firstName">{t('First name')}</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={(event) => updateField('firstName', event.target.value)}
                placeholder={t('First name')}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="lastName">{t('Last name')}</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={(event) => updateField('lastName', event.target.value)}
                placeholder={t('Last name')}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">{t('Email')}</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={(event) => updateField('email', event.target.value)}
                placeholder={t('example@email.com')}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone">{t('Phone')}</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={(event) => updateField('phone', event.target.value)}
                placeholder={t('Phone number')}
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">{t('Password')}</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={(event) => updateField('password', event.target.value)}
                placeholder={t('Password')}
                required={!isLoggedIn}
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">{t('Confirm password')}</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={(event) => updateField('confirmPassword', event.target.value)}
                placeholder={t('Confirm password')}
                required={!isLoggedIn}
              />
            </div>

            <div className="form-group full-width">
              <label htmlFor="companyName">{t('Company or organization name')}</label>
              <input
                type="text"
                id="companyName"
                name="companyName"
                value={formData.companyName}
                onChange={(event) => updateField('companyName', event.target.value)}
                placeholder={t('Company, sponsor, issuer, SPV, or external platform name')}
              />
            </div>
          </div>
        </section>

        <section className="pricing-section required-section">
          <div className="section-heading">
            <span>{t('2')}</span>
            <div>
              <h2>{t('U.S. state or territory')}</h2>
              <p>{t('Select one U.S. state, territory, or related jurisdiction for this access request.')}</p>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="stateOrTerritory">{t('State, territory, or jurisdiction')}</label>
            <select
              id="stateOrTerritory"
              name="stateOrTerritory"
              value={formData.stateOrTerritory}
              onChange={(event) => updateField('stateOrTerritory', event.target.value)}
              required
            >
              <option value="">{t('Select one')}</option>
              {US_STATE_AND_TERRITORY_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {t(option)}
                </option>
              ))}
            </select>
          </div>
        </section>

        <section className="pricing-section required-section">
          <div className="section-heading">
            <span>{t('3')}</span>
            <div>
              <h2>{t('User type or role')}</h2>
              <p>
                {t(
                  'Select one or more roles. Some roles may require identity verification, business verification, professional credentials, service agreements, or platform approval.'
                )}
              </p>
            </div>
          </div>

          <div className="role-grid">
            {ROLE_OPTIONS.map((role) => (
              <label key={role.id} className="role-card">
                <input
                  type="checkbox"
                  checked={formData.selectedRoles.includes(role.id)}
                  onChange={() => toggleRole(role.id)}
                />
                <span className="role-number">{t(role.number)}</span>
                <span className="role-content">
                  <strong>{t(role.title)}</strong>
                  <small>{t(role.description)}</small>
                </span>
              </label>
            ))}
          </div>
        </section>

        {hasAssetOwnerRole && (
          <section className="pricing-section highlight-section">
            <div className="section-heading">
              <span>{t('8')}</span>
              <div>
                <h2>{t('Asset owner, sponsor, issuer, or operator service plan')}</h2>
                <p>
                  {t(
                    'This section appears only when the asset owner, sponsor, issuer, property manager, asset manager, or rehab manager role is selected.'
                  )}
                </p>
              </div>
            </div>

            <div className="plan-grid">
              {ASSET_OWNER_PLANS.map((plan) => (
                <label key={plan.id} className="plan-card">
                  <input
                    type="radio"
                    name="assetOwnerPlan"
                    value={plan.id}
                    checked={formData.assetOwnerPlan === plan.id}
                    onChange={(event) => updateField('assetOwnerPlan', event.target.value)}
                    required={hasAssetOwnerRole}
                  />
                  <strong>{t(plan.title)}</strong>
                  <span>{t(plan.price)}</span>
                  <small>{t(plan.description)}</small>
                </label>
              ))}
            </div>
          </section>
        )}

        {hasExternalTokenRole && (
          <section className="pricing-section highlight-section external-token-section">
            <div className="section-heading">
              <span>{t('9')}</span>
              <div>
                <h2>{t('External token listing information')}</h2>
                <p>
                  {t(
                    'Use this section when the asset or economic interest has already been tokenized outside this platform and you want to request review or listing services.'
                  )}
                </p>
              </div>
            </div>

            <div className="warning-box">
              {t(
                'The platform does not guarantee the external token, smart contract, valuation, ownership, cash flow, compliance status, or prior transfers unless a separate review and responsibility agreement is completed.'
              )}
            </div>

            <div className="form-grid two-columns">
              <div className="form-group">
                <label htmlFor="externalTokenStatus">{t('Requested external listing status')}</label>
                <select
                  id="externalTokenStatus"
                  name="externalTokenStatus"
                  value={formData.externalTokenStatus}
                  onChange={(event) => updateField('externalTokenStatus', event.target.value)}
                >
                  <option value="">{t('Select one')}</option>
                  {EXTERNAL_TOKEN_STATUS_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {t(option)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="externalTokenIssuer">{t('External issuer or platform name')}</label>
                <input
                  type="text"
                  id="externalTokenIssuer"
                  name="externalTokenIssuer"
                  value={formData.externalTokenIssuer}
                  onChange={(event) => updateField('externalTokenIssuer', event.target.value)}
                  placeholder={t('Issuer, sponsor, or originating platform')}
                />
              </div>

              <div className="form-group">
                <label htmlFor="externalTokenNetwork">{t('Blockchain network')}</label>
                <input
                  type="text"
                  id="externalTokenNetwork"
                  name="externalTokenNetwork"
                  value={formData.externalTokenNetwork}
                  onChange={(event) => updateField('externalTokenNetwork', event.target.value)}
                  placeholder={t('Ethereum, Polygon, Avalanche, or another network')}
                />
              </div>

              <div className="form-group">
                <label htmlFor="externalTokenStandard">{t('Token standard')}</label>
                <input
                  type="text"
                  id="externalTokenStandard"
                  name="externalTokenStandard"
                  value={formData.externalTokenStandard}
                  onChange={(event) => updateField('externalTokenStandard', event.target.value)}
                  placeholder={t('ERC-20, ERC-1400, ERC-3643, or another standard')}
                />
              </div>

              <div className="form-group full-width">
                <label htmlFor="externalTokenContractAddress">{t('Smart contract address')}</label>
                <input
                  type="text"
                  id="externalTokenContractAddress"
                  name="externalTokenContractAddress"
                  value={formData.externalTokenContractAddress}
                  onChange={(event) => updateField('externalTokenContractAddress', event.target.value)}
                  placeholder={t('Smart contract address')}
                />
              </div>

              <div className="form-group full-width">
                <label htmlFor="externalLedgerReference">{t('External ledger or cap table reference')}</label>
                <input
                  type="text"
                  id="externalLedgerReference"
                  name="externalLedgerReference"
                  value={formData.externalLedgerReference}
                  onChange={(event) => updateField('externalLedgerReference', event.target.value)}
                  placeholder={t('Transfer agent, ledger source, snapshot, or reference URL')}
                />
              </div>
            </div>
          </section>
        )}

        <section className="pricing-section">
          <div className="section-heading">
            <span>{t('4')}</span>
            <div>
              <h2>{t('Document upload')}</h2>
              <p>
                {t(
                  'Upload identity documents, business documents, professional licenses, agreements, ownership documents, appraisal reports, title documents, smart contract reports, or other relevant files.'
                )}
              </p>
            </div>
          </div>

          <div
            className={`dropzone ${isDragging ? 'dragging' : ''}`}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            role="button"
            tabIndex="0"
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                fileInputRef.current?.click();
              }
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="file-input"
              onChange={handleFilePickerChange}
            />
            <strong>{t('Drag and drop files here')}</strong>
            <span>{t('or click to choose files')}</span>
          </div>

          {uploadedFiles.length > 0 && (
            <ul className="file-list">
              {uploadedFiles.map((file, index) => (
                <li key={`${file.name}-${file.size}-${file.lastModified}`}>
                  <span>{file.name}</span>
                  <button type="button" onClick={() => removeFile(index)}>
                    {t('Remove')}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="pricing-section">
          <div className="section-heading">
            <span>{t('5')}</span>
            <div>
              <h2>{t('Additional notes')}</h2>
              <p>
                {t(
                  'Add any details about your role, project, asset, token, service needs, agreements, payment method, or secondary transfer request.'
                )}
              </p>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="notes">{t('Notes')}</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={(event) => updateField('notes', event.target.value)}
              placeholder={t('Additional notes')}
              rows="5"
            />
          </div>
        </section>

        <section className="pricing-section secondary-market-note">
          <h2>{t('Secondary transfers')}</h2>
          <p>
            {t(
              'A secondary transfer means a token transfer after the original issuance, such as a transfer from one approved holder to another. Secondary transfers may require additional approval, transfer restrictions, legal review, broker-dealer support, transfer agent support, or other compliance steps.'
            )}
          </p>
        </section>

        <button type="submit" className="auth-button pricing-submit-button">
          {t('Submit pricing and access request')}
        </button>

        {message && <p className="auth-message">{message}</p>}
      </form>
    </div>
  );
};

export default SubscriptionPage;